import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  Stethoscope, Building2, ArrowLeft, Mail, Lock, Phone, 
  MapPin, User, FileText, Shield, CheckCircle, Upload, X, Loader2
} from "lucide-react";

const API = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || '';

const PROVINCE_OPTIONS = [
  "AG", "AL", "AN", "AO", "AP", "AQ", "AR", "AT", "AV", "BA", "BG", "BI", "BL", "BN", "BO",
  "BR", "BS", "BT", "BZ", "CA", "CB", "CE", "CH", "CL", "CN", "CO", "CR", "CS", "CT", "CZ",
  "EN", "FC", "FE", "FG", "FI", "FM", "FR", "GE", "GO", "GR", "IM", "IS", "KR", "LC", "LE",
  "LI", "LO", "LT", "LU", "MB", "MC", "ME", "MI", "MN", "MO", "MS", "MT", "NA", "NO", "NU",
  "OR", "PA", "PC", "PD", "PE", "PG", "PI", "PN", "PO", "PR", "PT", "PU", "PV", "PZ", "RA",
  "RC", "RE", "RG", "RI", "RM", "RN", "RO", "SA", "SI", "SO", "SP", "SR", "SS", "SU", "SV",
  "TA", "TE", "TN", "TO", "TP", "TR", "TS", "TV", "UD", "VA", "VB", "VC", "VE", "VI", "VR", "VT", "VV"
];

const SPECIALIZATIONS = [
  "Medicina generale",
  "Chirurgia",
  "Ortopedia",
  "Dermatologia",
  "Cardiologia",
  "Neurologia",
  "Oncologia",
  "Oftalmologia",
  "Odontoiatria",
  "Medicina interna",
  "Animali esotici",
  "Equini"
];

export default function VetAuth() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login, register, choose
  const [accountType, setAccountType] = useState(null); // veterinario, clinica
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    phone: "",
    codice_albo: "",
    ordine_provincia: "",
    partita_iva: "",
    codice_sdi: "",
    address: "",
    city: "",
    postal_code: "",
    provincia: "",
    specializations: [],
    website: "",
    terms_accepted: false,
    id_document_url: "",
    certification_url: ""
  });
  
  // File upload states
  const [uploadingId, setUploadingId] = useState(false);
  const [uploadingCert, setUploadingCert] = useState(false);
  const [idFileName, setIdFileName] = useState("");
  const [certFileName, setCertFileName] = useState("");

  // File upload handler
  const handleFileUpload = async (file, type) => {
    if (!file) return;
    
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error("Il file è troppo grande. Massimo 10MB.");
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Formato non supportato. Usa PDF, JPG o PNG.");
      return;
    }

    if (type === 'id') {
      setUploadingId(true);
    } else {
      setUploadingCert(true);
    }

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const res = await fetch(`${API}/api/upload/file`, {
        method: 'POST',
        body: formDataUpload
      });

      if (res.ok) {
        const data = await res.json();
        if (type === 'id') {
          setFormData(prev => ({ ...prev, id_document_url: data.file_url }));
          setIdFileName(file.name);
          toast.success("Documento d'identità caricato");
        } else {
          setFormData(prev => ({ ...prev, certification_url: data.file_url }));
          setCertFileName(file.name);
          toast.success("Certificazione caricata");
        }
      } else {
        toast.error("Errore nel caricamento del file");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      if (type === 'id') {
        setUploadingId(false);
      } else {
        setUploadingCert(false);
      }
    }
  };

  const removeFile = (type) => {
    if (type === 'id') {
      setFormData(prev => ({ ...prev, id_document_url: "" }));
      setIdFileName("");
    } else {
      setFormData(prev => ({ ...prev, certification_url: "" }));
      setCertFileName("");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await fetch(`${API}/api/vet/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        localStorage.setItem('vet_token', data.session_token);
        localStorage.setItem('vet_profile', JSON.stringify(data));
        toast.success(`Benvenuto, ${data.name}!`);
        navigate('/vet/dashboard');
      } else {
        toast.error(data.detail || "Errore di accesso");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }
    
    if (!formData.terms_accepted) {
      toast.error("Devi accettare i termini e condizioni");
      return;
    }

    // Validate document uploads
    if (!formData.id_document_url) {
      toast.error("Carica un documento d'identità valido");
      return;
    }
    
    setLoading(true);
    
    try {
      const res = await fetch(`${API}/api/vet/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account_type: accountType,
          name: formData.name,
          email: formData.email,
          password: formData.password,
          phone: formData.phone,
          codice_albo: formData.codice_albo || null,
          ordine_provincia: formData.ordine_provincia || null,
          partita_iva: formData.partita_iva || null,
          codice_sdi: formData.codice_sdi || null,
          address: formData.address || null,
          city: formData.city || null,
          postal_code: formData.postal_code || null,
          provincia: formData.provincia || null,
          specializations: formData.specializations,
          website: formData.website || null,
          id_document_url: formData.id_document_url || null,
          certification_url: formData.certification_url || null
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success("Registrazione completata! Il tuo account sarà verificato entro 24-48 ore.");
        setMode("login");
      } else {
        toast.error(data.detail || "Errore nella registrazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  const toggleSpecialization = (spec) => {
    setFormData(prev => ({
      ...prev,
      specializations: prev.specializations.includes(spec)
        ? prev.specializations.filter(s => s !== spec)
        : [...prev.specializations, spec]
    }));
  };

  // Choose account type screen
  if (mode === "register" && !accountType) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#E8F5E9] to-white flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <button 
            onClick={() => setMode("login")}
            className="flex items-center gap-2 text-[#2E7D32] mb-8 hover:underline"
          >
            <ArrowLeft size={20} /> Torna al login
          </button>

          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold text-[#1D1D1F] mb-2">Registrati come</h1>
            <p className="text-[#86868B]">Scegli il tipo di account professionale</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Veterinario */}
            <button
              onClick={() => setAccountType("veterinario")}
              className="bg-white rounded-3xl p-6 border-2 border-transparent hover:border-[#2E7D32] transition-all text-left shadow-sm"
              data-testid="choose-veterinario"
            >
              <div className="w-16 h-16 rounded-2xl bg-[#E8F5E9] flex items-center justify-center mb-4">
                <Stethoscope className="text-[#2E7D32]" size={32} />
              </div>
              <h3 className="text-xl font-bold text-[#1D1D1F] mb-2">Veterinario</h3>
              <p className="text-sm text-[#86868B]">
                Libero professionista o dipendente. Richiede codice albo.
              </p>
            </button>

            {/* Clinica */}
            <button
              onClick={() => setAccountType("clinica")}
              className="bg-white rounded-3xl p-6 border-2 border-transparent hover:border-[#2E7D32] transition-all text-left shadow-sm"
              data-testid="choose-clinica"
            >
              <div className="w-16 h-16 rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-4">
                <Building2 className="text-[#E65100]" size={32} />
              </div>
              <h3 className="text-xl font-bold text-[#1D1D1F] mb-2">Clinica</h3>
              <p className="text-sm text-[#86868B]">
                Struttura veterinaria con più collaboratori. Richiede P.IVA.
              </p>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Registration form
  if (mode === "register" && accountType) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#E8F5E9] to-white py-8 px-4">
        <div className="max-w-xl mx-auto">
          <button 
            onClick={() => step > 1 ? setStep(step - 1) : setAccountType(null)}
            className="flex items-center gap-2 text-[#2E7D32] mb-6 hover:underline"
          >
            <ArrowLeft size={20} /> Indietro
          </button>

          {/* Header */}
          <div className="text-center mb-8">
            <div className={`w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center ${
              accountType === "veterinario" ? "bg-[#E8F5E9]" : "bg-[#FFF3E0]"
            }`}>
              {accountType === "veterinario" ? (
                <Stethoscope className="text-[#2E7D32]" size={32} />
              ) : (
                <Building2 className="text-[#E65100]" size={32} />
              )}
            </div>
            <h1 className="text-2xl font-bold text-[#1D1D1F]">
              Registrazione {accountType === "veterinario" ? "Veterinario" : "Clinica"}
            </h1>
            <p className="text-[#86868B] mt-1">Step {step} di 4</p>
          </div>

          {/* Progress bar */}
          <div className="flex gap-2 mb-8">
            {[1, 2, 3, 4].map(s => (
              <div 
                key={s} 
                className={`h-1 flex-1 rounded-full ${s <= step ? "bg-[#2E7D32]" : "bg-gray-200"}`}
              />
            ))}
          </div>

          <form onSubmit={handleRegister} className="bg-white rounded-3xl p-6 shadow-sm">
            {/* Step 1: Basic info */}
            {step === 1 && (
              <div className="space-y-4">
                <h2 className="font-semibold text-lg mb-4">Informazioni base</h2>
                
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">
                    {accountType === "veterinario" ? "Nome e Cognome" : "Nome Clinica"} *
                  </Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder={accountType === "veterinario" ? "Dr. Mario Rossi" : "Clinica Veterinaria XYZ"}
                    className="h-12 rounded-xl"
                    required
                    data-testid="name-input"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="email@esempio.it"
                    className="h-12 rounded-xl"
                    required
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Telefono *</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+39 333 1234567"
                    className="h-12 rounded-xl"
                    required
                    data-testid="phone-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium mb-1.5 block">Password *</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="••••••••"
                      className="h-12 rounded-xl"
                      required
                      minLength={8}
                      data-testid="password-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-1.5 block">Conferma *</Label>
                    <Input
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                      placeholder="••••••••"
                      className="h-12 rounded-xl"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <Button 
                  type="button"
                  onClick={() => setStep(2)}
                  className="w-full h-12 bg-[#2E7D32] hover:bg-[#1B5E20] rounded-xl mt-4"
                  disabled={!formData.name || !formData.email || !formData.phone || !formData.password}
                >
                  Continua
                </Button>
              </div>
            )}

            {/* Step 2: Professional info */}
            {step === 2 && (
              <div className="space-y-4">
                <h2 className="font-semibold text-lg mb-4">Dati professionali</h2>
                
                {accountType === "veterinario" ? (
                  <>
                    <div>
                      <Label className="text-sm font-medium mb-1.5 block">Codice Albo Veterinari *</Label>
                      <Input
                        value={formData.codice_albo}
                        onChange={(e) => setFormData({...formData, codice_albo: e.target.value})}
                        placeholder="Es. 12345"
                        className="h-12 rounded-xl font-mono"
                        required
                        data-testid="codice-albo-input"
                      />
                      <p className="text-xs text-[#86868B] mt-1">
                        Numero iscrizione all'Ordine dei Medici Veterinari
                      </p>
                    </div>

                    <div>
                      <Label className="text-sm font-medium mb-1.5 block">Provincia Ordine *</Label>
                      <Select 
                        value={formData.ordine_provincia} 
                        onValueChange={(v) => setFormData({...formData, ordine_provincia: v})}
                      >
                        <SelectTrigger className="h-12 rounded-xl" data-testid="ordine-provincia-select">
                          <SelectValue placeholder="Seleziona provincia..." />
                        </SelectTrigger>
                        <SelectContent>
                          {PROVINCE_OPTIONS.map(p => (
                            <SelectItem key={p} value={p}>{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label className="text-sm font-medium mb-1.5 block">Partita IVA *</Label>
                      <Input
                        value={formData.partita_iva}
                        onChange={(e) => setFormData({...formData, partita_iva: e.target.value})}
                        placeholder="12345678901"
                        className="h-12 rounded-xl font-mono"
                        maxLength={11}
                        required
                        data-testid="partita-iva-input"
                      />
                    </div>

                    <div>
                      <Label className="text-sm font-medium mb-1.5 block">Codice SDI (fatturazione elettronica)</Label>
                      <Input
                        value={formData.codice_sdi}
                        onChange={(e) => setFormData({...formData, codice_sdi: e.target.value.toUpperCase()})}
                        placeholder="XXXXXXX"
                        className="h-12 rounded-xl font-mono uppercase"
                        maxLength={7}
                        data-testid="codice-sdi-input"
                      />
                    </div>
                  </>
                )}

                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Specializzazioni</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {SPECIALIZATIONS.map(spec => (
                      <button
                        key={spec}
                        type="button"
                        onClick={() => toggleSpecialization(spec)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                          formData.specializations.includes(spec)
                            ? "bg-[#2E7D32] text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {spec}
                      </button>
                    ))}
                  </div>
                </div>

                <Button 
                  type="button"
                  onClick={() => setStep(3)}
                  className="w-full h-12 bg-[#2E7D32] hover:bg-[#1B5E20] rounded-xl mt-4"
                  disabled={accountType === "veterinario" ? !formData.codice_albo : !formData.partita_iva}
                >
                  Continua
                </Button>
              </div>
            )}

            {/* Step 3: Address */}
            {step === 3 && (
              <div className="space-y-4">
                <h2 className="font-semibold text-lg mb-4">Indirizzo studio/clinica</h2>
                
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Indirizzo</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="Via Roma 1"
                    className="h-12 rounded-xl"
                    data-testid="address-input"
                  />
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-1">
                    <Label className="text-xs mb-1 block">Città</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      placeholder="Milano"
                      className="h-11 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">CAP</Label>
                    <Input
                      value={formData.postal_code}
                      onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                      placeholder="20100"
                      className="h-11 rounded-lg text-sm"
                      maxLength={5}
                    />
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">Prov.</Label>
                    <Input
                      value={formData.provincia}
                      onChange={(e) => setFormData({...formData, provincia: e.target.value.toUpperCase()})}
                      placeholder="MI"
                      className="h-11 rounded-lg text-sm uppercase"
                      maxLength={2}
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Sito web</Label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData({...formData, website: e.target.value})}
                    placeholder="https://www.esempio.it"
                    className="h-12 rounded-xl"
                  />
                </div>

                <Button 
                  type="button"
                  onClick={() => setStep(4)}
                  className="w-full h-12 bg-[#2E7D32] hover:bg-[#1B5E20] rounded-xl mt-4"
                >
                  Continua
                </Button>
              </div>
            )}

            {/* Step 4: Documents & confirm */}
            {step === 4 && (
              <div className="space-y-4">
                <h2 className="font-semibold text-lg mb-4">Documenti di verifica</h2>
                
                {/* ID Document Upload */}
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">
                    Documento d'identità *
                  </Label>
                  <p className="text-xs text-[#86868B] mb-2">
                    Carica la carta d'identità o passaporto (PDF, JPG, PNG - max 10MB)
                  </p>
                  
                  {formData.id_document_url ? (
                    <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl border border-green-200">
                      <CheckCircle className="text-green-600" size={20} />
                      <span className="flex-1 text-sm text-green-800 truncate">{idFileName || "Documento caricato"}</span>
                      <button 
                        type="button"
                        onClick={() => removeFile('id')}
                        className="p-1 hover:bg-green-100 rounded"
                      >
                        <X size={16} className="text-green-600" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileUpload(e.target.files[0], 'id')}
                        disabled={uploadingId}
                        data-testid="upload-id-document"
                      />
                      {uploadingId ? (
                        <Loader2 className="animate-spin text-[#2E7D32]" size={24} />
                      ) : (
                        <>
                          <Upload className="text-[#86868B] mb-2" size={24} />
                          <span className="text-sm text-[#86868B]">Clicca per caricare</span>
                        </>
                      )}
                    </label>
                  )}
                </div>

                {/* Certification Upload */}
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">
                    {accountType === "veterinario" 
                      ? "Attestato iscrizione Ordine Veterinari" 
                      : "Autorizzazione sanitaria / Visura camerale"}
                  </Label>
                  <p className="text-xs text-[#86868B] mb-2">
                    Documento che attesta la tua abilitazione professionale (opzionale ma consigliato)
                  </p>
                  
                  {formData.certification_url ? (
                    <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl border border-green-200">
                      <CheckCircle className="text-green-600" size={20} />
                      <span className="flex-1 text-sm text-green-800 truncate">{certFileName || "Certificazione caricata"}</span>
                      <button 
                        type="button"
                        onClick={() => removeFile('cert')}
                        className="p-1 hover:bg-green-100 rounded"
                      >
                        <X size={16} className="text-green-600" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileUpload(e.target.files[0], 'cert')}
                        disabled={uploadingCert}
                        data-testid="upload-certification"
                      />
                      {uploadingCert ? (
                        <Loader2 className="animate-spin text-[#2E7D32]" size={24} />
                      ) : (
                        <>
                          <Upload className="text-[#86868B] mb-2" size={24} />
                          <span className="text-sm text-[#86868B]">Clicca per caricare (opzionale)</span>
                        </>
                      )}
                    </label>
                  )}
                </div>

                {/* Terms */}
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                  <Checkbox
                    id="terms"
                    checked={formData.terms_accepted}
                    onCheckedChange={(checked) => setFormData({...formData, terms_accepted: checked})}
                    data-testid="terms-checkbox"
                  />
                  <label htmlFor="terms" className="text-sm text-[#86868B]">
                    Accetto i <a href="/terms" className="text-[#2E7D32] underline">Termini e Condizioni</a> e 
                    la <a href="/privacy" className="text-[#2E7D32] underline">Privacy Policy</a> di Zampa.
                    Confermo che i dati inseriti sono corretti e verificabili.
                  </label>
                </div>

                {/* Verification notice */}
                <div className="flex items-start gap-3 p-4 bg-[#E8F5E9] rounded-xl">
                  <Shield className="text-[#2E7D32] flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <p className="font-medium text-[#1D1D1F] text-sm">Verifica account</p>
                    <p className="text-xs text-[#86868B] mt-1">
                      Il tuo account sarà verificato dal team Zampa entro 24-48 ore. 
                      Verificheremo i documenti caricati e l'iscrizione all'Ordine dei Veterinari.
                    </p>
                  </div>
                </div>

                <Button 
                  type="submit"
                  disabled={loading || !formData.terms_accepted || !formData.id_document_url}
                  className="w-full h-12 bg-[#2E7D32] hover:bg-[#1B5E20] rounded-xl"
                  data-testid="submit-registration"
                >
                  {loading ? "Registrazione..." : "Completa registrazione"}
                </Button>
              </div>
            )}
          </form>
        </div>
      </div>
    );
  }

  // Login form (default)
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8F5E9] to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-10">
          <Link to="/" className="inline-flex items-center gap-2 text-[#86868B] hover:text-[#1D1D1F] mb-6">
            <ArrowLeft size={18} /> Torna a Zampa
          </Link>
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-[#2E7D32] to-[#4CAF50] flex items-center justify-center mx-auto mb-4">
            <Stethoscope className="text-white" size={40} />
          </div>
          <h1 className="text-3xl font-bold text-[#1D1D1F]">Portale Veterinari</h1>
          <p className="text-[#86868B] mt-2">Accedi al tuo account professionale</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin} className="bg-white rounded-3xl p-6 shadow-sm">
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-[#86868B]" size={18} />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="email@clinica.it"
                  className="h-12 pl-12 rounded-xl"
                  required
                  data-testid="login-email"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium mb-1.5 block">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-[#86868B]" size={18} />
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="••••••••"
                  className="h-12 pl-12 rounded-xl"
                  required
                  data-testid="login-password"
                />
              </div>
            </div>

            <Button 
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#2E7D32] hover:bg-[#1B5E20] rounded-xl mt-2"
              data-testid="login-submit"
            >
              {loading ? "Accesso..." : "Accedi"}
            </Button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-[#86868B] text-sm">
              Non hai un account?{" "}
              <button 
                type="button"
                onClick={() => setMode("register")}
                className="text-[#2E7D32] font-medium hover:underline"
              >
                Registrati
              </button>
            </p>
          </div>
        </form>

        {/* Features */}
        <div className="mt-8 grid grid-cols-3 gap-3">
          {[
            { icon: FileText, label: "Gestione pazienti" },
            { icon: Shield, label: "Dati certificati" },
            { icon: CheckCircle, label: "Badge verificato" }
          ].map((f, i) => (
            <div key={i} className="text-center p-3">
              <f.icon className="mx-auto text-[#2E7D32] mb-2" size={24} />
              <p className="text-xs text-[#86868B]">{f.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
